import { Component, OnInit, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

// services
import { EventsService } from '../../core/services/events-service/events.service';
import { ProfileService } from '../../core/services/profile-service/profile.service';

// components
import { AlertComponent, EzValidators } from '../../shared';

// constants
import {
	tshirtSizes,
	CalculateAge,
	ScrollIt,
	scrollItType,
	getPhoneMask,
	stateList,
} from '../../shared';

import { School } from '../interfaces/school.interface';

@Component({
	selector: 'eze-owner-registration',
	templateUrl: './owner-registration.component.html',
	styleUrls: ['./owner-registration.component.css'],
})
export class OwnerRegistrationComponent implements OnInit {
	form: FormGroup;
	showValidation = false;
	eventId = '';
	successHtml = '';

	@ViewChild(AlertComponent, { static: false })
	alert: AlertComponent;
	@ViewChild('waiverModal', { static: false })
	waiverModal;
	@ViewChild('termsModal', { static: false })
	termsModal;

	@ViewChild('schoolAlert', { static: false })
	schoolAlert: AlertComponent;

	selectedSchoolDetails = null;

	get shirtSizes(): string[] {
		return tshirtSizes;
	}

	get waiverText(): string {
		return this.eventService.waiver.text;
	}

	get waiverHeader(): string {
		return this.eventService.waiver.header;
	}

	get isClosed(): boolean {
		return this.eventService.isRegistrationClosed('owner');
	}

	get eventUrl(): string {
		return this.eventService.eventData.url;
	}

	get profile() {
		return this.profileService.profile;
	}

	get isGuest() {
		return this.profileService.isGuest;
	}

	get stateList() {
		return stateList;
	}

	get schools() {
		return this.eventService.schools;
	}

	constructor(
		private fb: FormBuilder,
		@Inject(CalculateAge) private calculateAge,
		@Inject(ScrollIt) private scrollIt: scrollItType,
		private eventService: EventsService,
		private route: ActivatedRoute,
		private profileService: ProfileService
	) {}

	ngOnInit() {
		this.getEventId();
		this.initializeForm();
		this.watchFormForUpdates();
		this.profileService.patchFormWithProfile(this.form);
	}

	getEventId() {
		this.eventId = this.route.snapshot.parent.params.id;
	}

	documentIdRequired() {
		if (this.eventId !== 'svopen2019') {
			return { document_id: ['', Validators.required] };
		} else {
			return {};
		}
	}

	initializeForm() {
		this.form = this.fb.group({
			// personal information
			name_first: ['', Validators.required],
			name_middle: [''],
			name_last: ['', Validators.required],

			birth_date: ['', Validators.required],

			gender: ['', Validators.required],

			// contact information
			email: ['', [Validators.required, Validators.email]],
			phone: ['', Validators.required],
			address: ['', Validators.required],
			address_2: [''],
			city: ['', Validators.required],
			state: ['', Validators.required],
			zip: ['', Validators.required],

			...this.documentIdRequired(),

			// misc information
			// 'shirt_size': 			['', Validators.required],
			school: this.fb.group(
				{
					id: [''],
					name: [''],
					master: [''],
					address: [''],
					address_2: [''],
					city: [''],
					state: [''],
					zip: [''],
					country: [''],
					phone: [''],
					email: [''],
					url: [''],
				},
				{
					validator: EzValidators.schoolRequired,
				}
			),

			guest_name_first: [],
			guest_name_last: [],

			// legal stuff and subsrcriptions
			subscription: [false],
			owner_confirmation: [false, Validators.requiredTrue],
			legal_age: [false, Validators.requiredTrue],
			no_crime: [false, Validators.requiredTrue],
			waiver: [false, Validators.requiredTrue],
			terms_and_conditions: [false, Validators.requiredTrue],
			catchall: [false, Validators.requiredTrue],
		});
	}

	watchFormForUpdates() {
		this.form
			.get('school.id')
			.valueChanges.subscribe((schoolId: string) => {
				this.getSchoolDetails(schoolId);
			});
	}

	getSchoolDetails(schoolId) {
		this.selectedSchoolDetails = null;
		this.form.get('school.name').reset();
		this.form.get('school.master').reset();
		this.form.get('school.address').reset();
		this.form.get('school.address_2').reset();
		this.form.get('school.city').reset();
		this.form.get('school.state').reset();
		this.form.get('school.zip').reset();
		this.form.get('school.country').reset();
		this.form.get('school.phone').reset();
		this.form.get('school.email').reset();
		this.form.get('school.url').reset();

		if (this.schoolAlert) {
			this.schoolAlert.closeAlert();
		}

		if (schoolId === '-1' || !schoolId) {
			return;
		}

		this.eventService.getSchoolDetails(this.eventId, schoolId).subscribe(
			(res: School) => {
				this.selectedSchoolDetails = res;
				this.form.get('school.name').setValue(res.name);
				this.form.get('school.master').setValue(res.master);
				this.form.get('school.address').setValue(res.address);
				this.form.get('school.address_2').setValue(res.address_2);
				this.form.get('school.city').setValue(res.city);
				this.form.get('school.state').setValue(res.state);
				this.form.get('school.zip').setValue(res.zip);
				this.form.get('school.country').setValue(res.country);
				this.form.get('school.phone').setValue(res.phone);
				this.form.get('school.email').setValue(res.email);
				this.form.get('school.url').setValue(res.url);
			},
			(err) => {
				if (this.schoolAlert) {
					this.schoolAlert.newAlert(err.error.detail, 'danger');
				}
			}
		);
	}

	showSchoolDetails() {
		const selectedSchoolId = this.form.get('school.id').value;
		return (
			selectedSchoolId === '-1' ||
			(selectedSchoolId !== '-1' && this.selectedSchoolDetails)
		);
	}

	onSubmit() {
		this.scrollIt(0);
		if (this.form.invalid) {
			this.alert.newAlert('Please fill out required fields', 'danger');
			this.showValidation = true;
			return;
		}

		this.alert.newAlert('Submitting...', 'info');
		this.eventService
			.postEntity(this.eventId, 'owners', this.form.value)
			.subscribe(
				(res: any) => {
					if (res && res._html_message) {
						this.successHtml = res._html_message;
					} else {
						this.alert.newAlert('There was an error', 'danger');
					}
				},
				(err) => {
					this.alert.newAlert(
						err.error.detail || 'There was an error',
						'danger'
					);
				}
			);
	}

	resetForm() {
		this.form.reset();
		this.successHtml = '';
		this.showValidation = false;
	}

	isMinor(formGroup: FormGroup): boolean {
		const birth_date = formGroup.get('birth_date').value;
		return this.calculateAge(new Date(birth_date)) < 18;
	}

	openModal(event: Event, modalName: string) {
		event.preventDefault();
		switch (modalName) {
			case 'waiver':
				this.waiverModal.open();
				break;
			case 'terms':
				this.termsModal.open();
				break;
			default:
				break;
		}
	}

	getPhoneMask(formControl) {
		return getPhoneMask(this.form.get(formControl).value);
	}
}
